import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import lodash from 'lodash';
import { Helmet } from 'react-helmet';
import GoogleMapReact from 'google-map-react';
import ReactToPdf from 'react-to-pdf';
import moment from 'moment';
import mixpanel from 'mixpanel-browser';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import {
  Grid,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardMedia,
  CardContent,
  Paper,
  Modal,
} from '@material-ui/core';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@material-ui/lab/Skeleton';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import PinDropIcon from '@mui/icons-material/PinDrop';
import HistoryIcon from '@mui/icons-material/History';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import CloseIcon from '@material-ui/icons/Close';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import {
  COLOR_PRIMARY_LIGHT,
  COLOR_PRIMARY_DARK,
  COLOR_ACCENT_1,
  COLOR_ACCENT_2,
  COLOR_TOOLTIP_BACKGROUND,
  BACKGROUND_COLOR,
  exchangeRates,
  LIGHT_GREY_2,
} from 'src/const';
import { callApi } from 'src/core/callApi';
import { setCurrency, setEstimatorSearchedMarket } from 'src/store/Dashboard/actions';
import {
  // processLegacyListing,
  returnLoadingText,
  convertCurrency,
  formatCurrency,
  isEqualObject,
  // safeLegacyMarkersLoad,
  getListingCSVDataAsArray,
  makeDownloadableBlob,
  geocodeAddress,
  getEstimatorRegionUnlockStatus,
  checkIfGooglePlacesSessionValid,
  mapOptions,
} from 'src/utils/functions';
import { useHighlight } from 'src/utils/hooks';
import { InfoTooltip } from '../../../HOC/Tooltips';
import { CustomSwitch, ViewDemoButton } from '../../../Components/Buttons';
import { infoIconStyle } from '../styles/style';
import { LoadingCard, TooltipCard, EstimatorCalculator, KeyMetricsCard } from '../../../HOC/Cards';
import {
  ADR,
  OccupancyRate,
  Revenue,
  ListViewData,
  AddressObj,
  EstimatorFilter,
  EstimatorRegions,
  BookingsPerMonth,
  MapActiveView,
} from 'src/interfaces/idashboard';
import { Filter, Pointer } from '../../../HOC';
import { EstimatorDashboardPropTypes, FilterVal } from 'src/interfaces/iproptypes';
import ConfirmUnlockRegionModal from 'src/App/Modals/ConfirmUnlockRegionModal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import postOnSlack from 'src/core/postOnSlack';
import CenterPointer from 'src/App/HOC/Pointer/CenterPointer';
import UnlockCTAButton from 'src/App/Components/Buttons/UnlockCTAButton';
import { Badge, Chip, TextField } from '@mui/material';
import {
  COLOR_GREY_600,
  COLOR_PRIMARY_2,
  COLOR_SUCCESS_1,
  COLOR_TEXT_3,
  SIDEBAR_COLOR,
} from 'src/colors';
import RealtorForm from 'src/App/Components/Forms/RealtorForm';
import { useLocation } from 'react-router-dom';
import EstimatorSuggestionCard from 'src/App/HOC/Cards/EstimatorSuggestionCard';
import { GOOGLE_MAPS_API_KEY } from 'src/keys';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');
const RIPPLE_SIZE = 100;
const RIPPLE_COLOR = COLOR_ACCENT_1;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    searchBar: {
      width: 600,
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    options: {
      '&:hover': {
        backgroundColor: COLOR_PRIMARY_LIGHT,
        fontColor: '#abcdef',
        textColor: '#abcdef',
      },
    },
    button: {
      background: COLOR_PRIMARY_DARK,
      borderRadius: 5,
      color: 'white',
      padding: '5px 30px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
      },
      fontSize: 16,
    },
    buttonDisabled: {
      background: LIGHT_GREY_2,
      borderRadius: 5,
      color: 'white',
      padding: '5px 30px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: LIGHT_GREY_2,
      },
      fontSize: 16,
    },
    advanceFilterButton: {
      // background: COLOR_PRIMARY_DARK,
      borderRadius: 5,
      color: COLOR_GREY_600,
      padding: '5px 10px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      border: '1px solid grey',
      // '&:hover': {
      //   background: COLOR_PRIMARY_LIGHT,
      // },
      fontSize: 16,
    },
    selectLoactionButton: {
      [theme.breakpoints.down(900)]: {
        position: 'absolute',
        bottom: 25,
        width: '80%',
        left: '10%',
        right: '10%',
        borderRadius: 100,
        boxSizing: 'border-box',
      },
    },
    iconButton: {
      [theme.breakpoints.down(900)]: {
        position: 'relative',
        zIndex: 100,
        background: '#fff !important',
        borderRadius: '50%',
        height: 70,
        width: 70,
        boxShadow: '0px -2px 7px -1px rgba(0, 0, 0, 0.55)',
        padding: 10,
        color: '#000',
        transform: 'scale(0.7) translateY(-20px)',
      },
    },
    buttonReverse: {
      color: COLOR_PRIMARY_DARK,
      borderRadius: 5,
      padding: '5px 30px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
        color: 'white',
      },
      fontSize: 16,
    },
    mapContainer: {
      position: 'relative',
      top: '0',
      left: '0',
      [theme.breakpoints.up('xs')]: {
        height: 580,
      },
      [theme.breakpoints.up(900)]: {
        height: 600,
      },
    },
    customLocationMapContainer: {
      height: 400,
      [theme.breakpoints.down(900)]: {
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
      },
    },
    leaderboardContainer: {
      position: 'relative',
      width: '100%',
      [theme.breakpoints.up('xs')]: {
        height: 570,
      },
      [theme.breakpoints.up(900)]: {
        height: 570,
      },
    },
    linearProgress: {
      width: '100%',
      marginBottom: 20,
      color: COLOR_PRIMARY_DARK,
    },
    paperModal: {
      backgroundColor: BACKGROUND_COLOR,
      width: 600,
      padding: theme.spacing(2),
      animation: 'fade-scale-in 0.3s ease forwards',
      //tablet, mobile
      [theme.breakpoints.down(900)]: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        boxSizing: 'border-box',
      },
    },
    ripple: {
      position: 'absolute',
      top: 0,
      left: 0,
      marginTop: -RIPPLE_SIZE / 2,
      marginLeft: -RIPPLE_SIZE / 2,
      width: RIPPLE_SIZE,
      height: RIPPLE_SIZE,
      animation: `$rippleAnimation 1000ms ${theme.transitions.easing.easeOut}`,
      animationIterationCount: 'infinite',
      opacity: 0,
      borderRadius: '50%',
      boxShadow: `0 0 6px 3px ${RIPPLE_COLOR}`,
    },
    '@keyframes rippleAnimation': {
      '0%': {
        transform: 'scale(0.1, 0.1)',
        opacity: 0,
      },
      '1%': {
        opacity: 1,
      },
      '50% ': {
        opacity: 1,
      },
      '100%': {
        transform: 'scale(1.2, 1.2)',
        opacity: 0,
      },
    },
    showPdf: {
      width: 800,
      background: BACKGROUND_COLOR,
    },
    mapCardContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    datagridBlur: {
      '& .MuiDataGrid-row': {
        '        &:nth-child(n+4)': {
          filter: 'blur(5px)',
        },
      },
    },
    mapTopRightToolbarContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 10,
      justifyContent: 'space-between',
      width: '100%',
    },
    invisbleMarkerDataText: {
      fontSize: 14,
      fontWeight: 400,
      // marginRight: 20,
    },
    tableRoot: {
      '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
        display: 'none',
      },
    },
    hiddenRow: {
      backgroundColor: '#ccc',
      backgroundImage: 'repeating-linear-gradient(-45deg, #eee, #eee 2px, #ccc 2px, #ccc 4px)',
      opacity: 0.7,
    },
  })
);

const EstimatorDashboard = ({
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  user,
  isLoggedIn,
  setLoginModalOpen,
  getUserInfo,
  matches,
  currency,
  isUserVerified,
  setIsEmailVerifyModalOpen,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  centerCoord,
  setCenterCoord,
  address,
  setAddress,
  dummyMarket,
  loginCountryCode,
  prevCenterCoord,
  setPrevCenterCoord,
}: EstimatorDashboardPropTypes) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const location = useLocation();

  const sampleEstimatorMarket = dummyMarket?.address;
  const sampleEstimatorCenterCoord = dummyMarket?.coordinates;

  const [kpiCardView, setKpiCardView] = useState(['f', 'f', 'f']);
  // variable and state declarations

  const [serverError, setServerError] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(true);
  const [options, setOptions] = useState<AddressObj[]>([] as AddressObj[]);

  const [locationAllowed, setLocationAllowed] = useState<boolean>(false);
  const [customCenterLat, setCustomCenterLat] = useState(39.210878338011376); // default to US approx center
  const [customCenterLng, setCustomCenterLng] = useState(-101.51684523289367); // default to US approx center
  const [searchBarState, setSearchBarState] = useState<string>('');
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const loading: boolean = open && loadingState;
  const emptySearchBar: boolean = open && searchBarState.length === 0;
  const [filters, setFilters] = useState<EstimatorFilter>({
    bedrooms: '2',
    bathrooms: '2',
  });
  const [isFilterUpdate, setIsFilterUpdate] = useState<boolean>(false);
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState<boolean>(false);
  const [advancedFilterVal, setAdvancedFilterVal] = useState({
    roomType: ['all'],
    bedrooms: [100],
    bathrooms: [100],
    revenueFilter: ['all'],
    priceTierFilter: ['all'],
    accommodates: [1, 16],
    minimum_nights: [1, 30],
    number_of_reviews: 'all',
    rating: [3.9, 5],
    hostType: 'both',
    amenities: {
      pool: null,
      parking: null,
      hot_tub: null,
      gym: null,
      pets: null,
      dryer: null,
      washer: null,
      kitchen: null,
      ac: null,
      heating: null,
      wifi: null,
      tv: null,
      bbq_grill: null,
      fireplace: null,
      breakfast: null,
      beachfront: null,
      amazing_views: null,
    },
    showAllBookingComListings: false,
  } as FilterVal);
  const [advancedFilterActive, setAdvancedFilterActive] = useState(false);
  const [advancedFilterApplied, setAdvancedFilterApplied] = useState(false);
  const [AggregatedMarketRevenue, setAggregatedMarketRevenue] = useState<number>(0);
  const [AggregatedMarketOccupancy, setAggregatedMarketOccupancy] = useState<number>(0);
  const [AggregatedMarketDailyRate, setAggregatedMarketDailyRate] = useState<number>(0);
  const [historicalMarketLevelData, setHistoricalMarketLevelData] = useState<any[]>([]);
  const [userSearchedRegion, setUserSearchedRegion] = useState<EstimatorRegions[]>(
    [] as EstimatorRegions[]
  );
  const [apiLoading, setApiLoading] = useState<boolean>(false);
  const [geocodingLoading, setGeocodingLoading] = useState<boolean>(false);
  const [listings, setListings] = useState<any[]>([]);

  const [notEnoughListingsFlag, setNotEnoughListingsFlag] = useState<boolean>(false);

  const [listViewData, setlistViewData] = useState<ListViewData[]>([]);
  const [customLocationModalOpen, setCustomLocationModalOpen] = useState<boolean>(false);
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState<boolean>(true);
  const [circleCopy, setCircleCopy] = useState<any>(null);
  const [radiusInMeter, setRadiusInMeter] = useState<number>(5000);
  const [searchedRegionBoundry, setSearchedRegionBoundry] = useState<any>(null);
  const [searchBarValue, setSearchBarValue] = useState<AddressObj>({
    address: sampleEstimatorMarket,
  } as AddressObj);
  const [pdfTitle, setPdfTitle] = useState<string>(sampleEstimatorMarket);
  const pdfRef = useRef<HTMLDivElement>();
  const todayDate: string = moment().format('DD/MM/YYYY');
  const [downloadTriggered, setDownloadTriggered] = useState<boolean>(false);
  const [unlockRegionModalOpen, setUnlockRegionModalOpen] = useState<boolean>(false);
  const [isPremimumRequest, setIsPremimumRequest] = useState<boolean>(false);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [noOfReviewsInAdvanceFilter, setNoOfReviewsInAdvanceFilter] = useState<
    'any' | '1' | '3' | '10' | '30'
  >('any');
  const [poolInAdvanceFilter, setPoolInAdvanceFilter] = useState<'any' | 'yes' | 'no'>('any');
  const [simulateROIPopupOpen, setSimulateROIPopupOpen] = useState<boolean>(false);
  const [mapRef, setMapRef] = useState<{
    map: any;
    maps: any;
  } | null>(null);

  const [active, setActive] = useState<MapActiveView>({
    map: true,
    list: false,
  });

  const [fetchCompsCount, setFetchCompsCount] = useState<number>(10);
  const [isFetchMoreCompsButtonDisabled, setIsFetchMoreCompsButtonDisabled] = useState<boolean>(
    false
  );
  const [lockedListingIds, setLockedListingIds] = useState<string[]>([]);
  const [hiddenListingIds, setHiddenListingIds] = useState<string[]>([]);
  const [existingCompsIds, setExistingCompsIds] = useState<string[]>([]);
  const [showRealtorForm, setshowRealtorForm] = useState<boolean>(false);
  const [nearbySearches, setNearBySearches] = useState<any[]>([]);

  const searchParams = location.search;
  const openRealtorFormFromURL = new URLSearchParams(searchParams).get('realtorForm') || '-1';

  useEffect(() => {
    if (openRealtorFormFromURL === 'true') {
      setshowRealtorForm(true);
    }
  }, [openRealtorFormFromURL]);

  const successAlert = (text: any) =>
    toast.success(text, {
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
      draggablePercent: 60,
    });

  const addHiddenListingId = newItem => {
    setHiddenListingIds(prevItems => {
      const updatedItems = [...prevItems, newItem];
      localStorage.setItem('hiddenListingIds', JSON.stringify(updatedItems));
      return updatedItems;
    });
  };

  const removeHiddenListingId = itemToRemove => {
    setHiddenListingIds(prevItems => {
      const updatedItems = prevItems.filter(item => item !== itemToRemove);
      localStorage.setItem('hiddenListingIds', JSON.stringify(updatedItems));
      return updatedItems;
    });
  };

  const handleUpdateButtonClick = () => {
    setExistingCompsIds([]);
    localStorage.setItem('hiddenListingIds', JSON.stringify([]));
    setHiddenListingIds([]);
    setFetchCompsCount(10);
    setIsFilterUpdate(true);
  };

  const handleKpiCardView = (val: 'front' | 'rear', title) => {
    if (title.includes('Revenue')) {
      const updatedKpiCardView = [...kpiCardView];
      updatedKpiCardView[0] = val === 'front' ? 'f' : 'r';
      setKpiCardView(updatedKpiCardView);
    } else if (title.includes('Occupancy')) {
      const updatedKpiCardView = [...kpiCardView];
      updatedKpiCardView[1] = val === 'front' ? 'f' : 'r';
      setKpiCardView(updatedKpiCardView);
    } else if (title.includes('Rate')) {
      const updatedKpiCardView = [...kpiCardView];
      updatedKpiCardView[2] = val === 'front' ? 'f' : 'r';
      setKpiCardView(updatedKpiCardView);
    }
  };

  const dynamicAnnualRevenueForCalculator = convertCurrency(
    currency,
    // useSelector(state => (state as any).dashboard.annualRevenueForCalculator)
    AggregatedMarketRevenue['revenue_50'] ? AggregatedMarketRevenue['revenue_50'] : 0
  );

  const estimatorQuery = window.history.state;
  const estimatorInvestmentCalculatorInputQueries = estimatorQuery
    ? estimatorQuery.estimatorInvestmentCalculatorInputs
    : null;
  let currencyQuery = 'USD';
  if (estimatorInvestmentCalculatorInputQueries) {
    currencyQuery = estimatorInvestmentCalculatorInputQueries.currencyQuery;
  }

  // useEffect(()=>{
  //   console.log("**", googleAutocompleteSessionToken)
  // }, [googleAutocompleteSessionToken])

  useEffect(() => {
    let estimatorCoords = localStorage.getItem('estimatorCoords');
    let estimatorAddress = localStorage.getItem('estimatorAddress');
    let hiddenIds = localStorage.getItem('hiddenListingIds');
    if (estimatorCoords) {
      let coords = JSON.parse(estimatorCoords);
      setCenterCoord(coords);
      if (estimatorAddress) {
        setSearchBarValue({
          address: estimatorAddress,
        });
      } else {
        if (
          coords.lat == sampleEstimatorCenterCoord?.lat &&
          coords.lng == sampleEstimatorCenterCoord?.lng
        ) {
          setSearchBarValue({
            address: sampleEstimatorMarket,
          });
        } else {
          setSearchBarValue({
            address: 'Set locaiton on map',
          });
        }
      }

      localStorage.removeItem('estimatorCoords');
      localStorage.removeItem('estimatorAddress');
    }

    if (hiddenIds) {
      setHiddenListingIds(JSON.parse(hiddenIds));
    }
  }, []);

  useEffect(() => {
    if (isLocked) {
      setNoOfReviewsInAdvanceFilter('any');
      setPoolInAdvanceFilter('any');
    }
  }, [isLocked]);

  useEffect(() => {
    if (currencyQuery) {
      dispatch(setCurrency(currencyQuery));
    }
  }, []);

  const bedrooms = {
    0: 'Studio',
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6+',
  };

  const bathrooms = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6+',
  };

  // Open subscription modal, if the user is not subscribed and the first time search
  useEffect(() => {
    const isEstimatorPaymentModalPopupShown = localStorage.getItem(
      'estimator-payment-modal-popup-shown'
    );

    if (
      !loading &&
      isLoggedIn &&
      user.email !== 'Trial' &&
      !user.isSubscribed &&
      isLocked &&
      !subscriptionModalOpen &&
      !isEstimatorPaymentModalPopupShown
    ) {
      setTimeout(() => {
        setSubscriptionModalOpen(true);
        localStorage.setItem('estimator-payment-modal-popup-shown', 'true');
      }, 5000);
    }
  }, [loading, user, isLocked]);

  useEffect(() => {
    dispatch(setEstimatorSearchedMarket(searchBarValue));
  }, [searchBarValue]);

  //hide tawkbot
  if ((window as any).Tawk_API) {
    (window as any).Tawk_API.onLoad = () => {
      (window as any).Tawk_API.hideWidget();
    };
  }

  const { isHighlight, highlightElement, removeHighlightElement } = useHighlight();

  useEffect(() => {
    if (isSampleRegion) {
      highlightElement('estimator-searchBar');
    } else {
      removeHighlightElement('estimator-searchBar');
    }
    setApiLoading(true);
  }, [centerCoord, searchBarValue]);

  useEffect(() => {
    setAdvancedFilterActive(false);
  }, [centerCoord]);

  const isSampleRegion =
    isEqualObject(centerCoord, sampleEstimatorCenterCoord) ||
    searchBarValue.address === sampleEstimatorMarket;

  // API call should be done from this useEffect only
  useEffect(() => {
    apiCallWrapper(isPremimumRequest);
    !isSampleRegion && removeHighlightElement('creditsBtn');
    const { map } = mapRef ? mapRef : { map: null };
    searchedRegionBoundry && map && map.fitBounds(searchedRegionBoundry.getBounds());
    return () => {
      isPremimumRequest && setIsPremimumRequest(false);
    };
  }, [
    centerCoord.lat,
    centerCoord.lng,
    isFilterUpdate,
    fetchCompsCount,
    isPremimumRequest,
    user.role,
    user.isSubscribed,
    user.customerID,
    hiddenListingIds.length,
  ]);

  useEffect(() => {
    getNearbySearches(centerCoord);
  }, [centerCoord, user.email]);

  useEffect(() => {
    if (fetchCompsCount >= 40 || listings.length === 0) {
      setIsFetchMoreCompsButtonDisabled(true);
    } else {
      setIsFetchMoreCompsButtonDisabled(false);
    }
  }, [fetchCompsCount, listings]);

  const checkedIfTheLocationPreviouslySearached = (value: string) => {
    if (value === sampleEstimatorMarket || value.trim() === '') {
      // console.log("Logic 1: ");
      return true;
    }
    for (const data of userSearchedRegion) {
      if (data.address === value || data.coordinate === value) {
        // console.log("Logic 2: ");
        return true;
      }
    }
    // console.log("Logic 3: ");
    return false;
  };

  // searchbar autocomplete
  const handleInputChange = useCallback(
    async (event, value: string, savedSearches: EstimatorRegions[]) => {
      setSearchBarState(value);
      setLoadingState(true);
      let addressObjs: AddressObj[] = [];

      let sessionToken = googleAutocompleteSessionToken;
      // console.log("===================================")
      // console.log(">>**>>", googleAutocompleteSessionToken)

      const isPreviouslyFetchedPlace: boolean = checkedIfTheLocationPreviouslySearached(value);
      const isGooglePlacesNeeded: boolean =
        !isPreviouslyFetchedPlace &&
        !checkIfGooglePlacesSessionValid(googleAutocompleteSessionTokenTimeStamp);

      const google = (window as any).google;
      if (!google || !google.maps) return;

      if (isGooglePlacesNeeded) {
        // console.log("Generated!", googleAutocompleteSessionToken)
        // sessionToken = await new google.maps.places.AutocompleteSessionToken();
        sessionToken = await getGoogleAutocompleteSessionToken(null);
        if (!sessionToken) return;
      }

      const service = new google.maps.places.AutocompleteService();

      // console.log("Final Token: ", sessionToken, isPreviouslyFetchedPlace)

      try {
        if (value.length > 0 && !isPreviouslyFetchedPlace) {
          // console.log("*******************")
          await service.getPlacePredictions(
            { input: `${value}`, sessionToken: sessionToken },
            (results, status) => {
              if (status == google.maps.places.PlacesServiceStatus.OK && results) {
                results.forEach(result => {
                  addressObjs.push({ address: result.description });
                });
              }
            }
          );
        }
      } catch (error) {
        console.log('error in estimator autocomplete ', error);
      }

      setOptions(e => {
        let tempAddressObjs: AddressObj[] = [...addressObjs];
        tempAddressObjs.push({ address: 'Set location on map', isDefaultOption: true });

        savedSearches &&
          savedSearches.forEach((region: EstimatorRegions) => {
            if (region.address) {
              tempAddressObjs.push({
                address: region.address,
                isSearchedOption: true,
              });
            } else {
              tempAddressObjs.push({
                address: region.coordinate,
                isSearchedOption: true,
                isCustom: true,
              });
            }
          });
        return tempAddressObjs;
      });

      setLoadingState(false);
    },
    [googleAutocompleteSessionToken, userSearchedRegion]
  );

  const handleSearchChange = useCallback(
    async (_, value: AddressObj) => {
      setSearchBarValue({
        address: value?.address as string,
      });

      if (!value) {
        return;
      }

      if (value.isDefaultOption) {
        setCustomLocationModalOpen(true);
        return;
      }

      if (value.isCustom) {
        const latString = value.address.split(',')[0].split('(')[1];
        const lngString = value.address.split(',')[1].split(')')[0];
        setFetchCompsCount(10);
        setCenterCoord({ lat: parseFloat(latString), lng: parseFloat(lngString) });
        setAddress('');
        return;
      }

      setGeocodingLoading(true);
      const [lat, lng] = (await geocodeAddress(value.address, true)) as [number, number];
      if (!isNaN(lat) && !isNaN(lng)) {
        setCenterCoord({ lat, lng });
        setAddress(value.address);
      }
      setGeocodingLoading(false);
      clearGoogleAutocompleteSessionToken();
    },
    [user.estimatorRegions]
  );

  const apiCallWrapper = (isPremiumRequest: boolean = false) => {
    setServerError(false);
    setCenterCoord(centerCoord => {
      if (isNaN(centerCoord.lat) || isNaN(centerCoord.lng)) {
        setApiLoading(false);
        alert(latLngErrorMessage);
        setSearchBarValue({
          address: sampleEstimatorMarket,
        });
        return centerCoord;
      }
      setPdfTitle(searchBarValue.address);
      const isRegionUnlocked: boolean = getEstimatorRegionUnlockStatus(
        user.estimatorRegions,
        centerCoord,
        sampleEstimatorCenterCoord
      );

      if (!isRegionUnlocked) {
        setAdvancedFilterActive(false);
      }

      if (isRegionUnlocked || isPremiumRequest) {
        getIncomeHistory(centerCoord, 'premium');
        setIsLocked(false);
      } else if (user.estimatorCredits > 0) {
        getIncomeHistory(centerCoord);
        setIsLocked(true);
        setUnlockRegionModalOpen(true);
      } else {
        getIncomeHistory(centerCoord);
        setIsLocked(true);
      }

      return centerCoord;
    });
    setIsFilterUpdate(false);
    setUpdateButtonDisabled(true);
  };

  const onUnlockModalConfirmClicked = () => {
    setIsPremimumRequest(true);
    setIsLocked(false);
    setUnlockRegionModalOpen(false);

    const simulatorWizardAutoOpen = localStorage.getItem('simulator-wizard-auto-open');
    if (!simulatorWizardAutoOpen) {
      setSimulateROIPopupOpen(true);
      localStorage.setItem('simulator-wizard-auto-open', 'true');
    }
  };
  const onUnlockModalCancelClicked = () => {
    setIsPremimumRequest(false);
    setIsLocked(true);
    setUnlockRegionModalOpen(false);
  };

  const handleFetchMoreComps = () => {
    if (fetchCompsCount < 40) {
      let Ids: string[] = [];
      for (let listing of listings) {
        Ids.push(listing['listingID']);
      }
      setExistingCompsIds(Ids);
      setFetchCompsCount(fetchCompsCount + 10);
    }
  };

  const makeCircle = (map, maps) => {
    if (!maps) {
      return;
    }

    const highlightedEstimatorRegion = new maps.Circle({
      strokeColor: '#52C879',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#4aa59a',
      fillOpacity: 0.12,
      center: { lat: centerCoord.lat, lng: centerCoord.lng },
      radius: radiusInMeter,
    });
    highlightedEstimatorRegion.setMap(map);

    setSearchedRegionBoundry(highlightedEstimatorRegion);

    map.fitBounds(highlightedEstimatorRegion.getBounds());
  };

  const latLngErrorMessage =
    'Something went wrong while setting the location on the map. Please refresh the page or try again later! If it still persists, please reach out to us at support@airbtics.com.';

  // api calls
  const getIncomeHistory = async (
    coord: { lat: number; lng: number },
    mode: 'sample' | 'premium' = 'sample'
  ) => {
    if (listings.length == 0) {
      setApiLoading(true);
    }
    setNotEnoughListingsFlag(false);
    setListings([]);
    setlistViewData([]);

    let _address: string = '';
    await setAddress(address => {
      _address = address.replaceAll(/#/g, '');
      return address;
    });

    let api: string =
      'https://2txezn5gg1.execute-api.us-east-2.amazonaws.com/default/getIncomeHistoryApp';
    // 'https://ix90mgtofd.execute-api.us-east-2.amazonaws.com/default/getIncomeHistoryAppTest';
    const {
      revenueFilter,
      priceTierFilter,
      amenities,
      showAllBookingComListings,
      bedrooms,
      bathrooms,
      ...filteredAdvancedFilterVal
    } = advancedFilterVal;

    let config = {} as any;

    setAdvancedFilterActive(currVal => {
      config = {
        queryParams: {
          address: _address,
          coordinate: `(${coord.lat},${coord.lng})`,
          bedrooms: filters.bedrooms,
          bathrooms: filters.bathrooms,
          requestType: 'sample',
          compsCount: prevCenterCoord == centerCoord ? fetchCompsCount : 10,
          signup_country_code: loginCountryCode,
          ...(currVal ? { ...filteredAdvancedFilterVal, ...amenities } : {}),
        },
      };
      return currVal;
    });

    if (mode === 'premium' || isEqualObject(coord, sampleEstimatorCenterCoord)) {
      config.queryParams.requestType = 'premium';
    }

    if (hiddenListingIds.length > 0) {
      config.queryParams['excludedIds'] = `[${hiddenListingIds}]`;
    }

    if (existingCompsIds.length > 0) {
      config.queryParams['existingCompsIds'] = `[${existingCompsIds}]`;
    }

    if (
      address !== sampleEstimatorMarket &&
      user.estimatorRegions.length === 0 &&
      user.estimatorCredits === 1 &&
      !localStorage.getItem('unused-free-estimator-credits-warning-sent')
    ) {
      postOnSlack('customer-dump', `[FREE ESTIMATOR CREDIT UNUSED]: USER: ${user.email}`);
      localStorage.setItem('unused-free-estimator-credits-warning-sent', 'true');
    }

    try {
      setLockedListingIds([]);
      let response = await callApi(
        api,
        config,
        true,
        false,
        'W0kgK9JsBe2uHy3AYho1a31w36wVl6AB1zUKlwFE'
      );

      if (!response || response.message === 'Failed to fetch') {
        console.error('Unable to fetch !');
        setApiLoading(false);
        setServerError(true);
        return;
      }

      // console.log('>>>>>>Response', response.message);
      if (response.message && response.message.error_reason) {
        console.log('error', response.message.error_reason);
        setApiLoading(false);
        setNotEnoughListingsFlag(true);
        return;
      }

      if (prevCenterCoord != centerCoord) {
        setPrevCenterCoord(centerCoord);
        setFetchCompsCount(10);
      }

      let data = response.message;
      let dates: string[] = data.dates;
      const historicalDates: string[] = data.historical_dates;

      setRadiusInMeter(data.radiusUsed ? data.radiusUsed : 5000);

      if (data.comps) {
        setAggregatedMarketRevenue(data.ltm_stats.revenue);
        setAggregatedMarketOccupancy(data.ltm_stats.occupancy);
        setAggregatedMarketDailyRate(data.ltm_stats.adr);
        setHistoricalMarketLevelData(data.historical_data);
        setListings(data.comps);
        await getUserInfo();
        setApiLoading(false);
        processCompsToShowInGridView(data.comps);
        filterAndSetLockedListingIds(data.comps);
      }
    } catch (error) {
      // console.log('Error (Fetching Failed): ', error);
      setApiLoading(false);
      setNotEnoughListingsFlag(true);
      setServerError(true);
      return;
    }
  };

  const getNearbySearches = async centerCoord => {
    setNearBySearches([]);
    const URL =
      'https://uqjwro3wyj.execute-api.us-east-2.amazonaws.com/default/getEstimatorSuggestions';
    let config = {
      queryParams: {
        coordinate: `(${centerCoord.lat}, ${centerCoord.lng})`,
        email: user.email != 'Trial' ? encodeURIComponent(user.email) : '',
      },
    };
    let response = await callApi(URL, config, false);

    if (response.message?.markets) {
      if (response.message?.markets.length > 0) {
        setNearBySearches(response.message.markets);
      }
    }
    if (response.message?.error_reason) {
      setNearBySearches([]);
    }
  };

  const filterAndSetLockedListingIds = comps => {
    let lockedIds: any[] = [];
    for (let comp of comps) {
      if (!comp.is_premium) {
        lockedIds.push(comp.listingID);
      }
    }
    setLockedListingIds(lockedIds);
  };

  const processCompsToShowInGridView = comps => {
    let rowData: ListViewData[] = [];
    rowData = comps.map((comp, idx) => {
      let hidden = false;
      if (hiddenListingIds) {
        hiddenListingIds.forEach(id => {
          if (id === comp['listingID']) {
            hidden = true;
          }
        });
      }
      return {
        listingID: comp.listingID,
        thumbnail_url: comp.thumbnail_url,
        avgNightlyRate: comp.adrLTM,
        id: idx,
        avgOccupancyRate: comp.occupancyRateLTM,
        avgRevenue: comp.revenuePotential,
        activeDays: comp.activeDaysCount,
        isHidden: hidden,
      };
    });
    setlistViewData(rowData);
  };

  const handleOnAdvanceFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdvancedFilterOpen(true);
  };

  const handleLocationChange = () => {
    searchedRegionBoundry && searchedRegionBoundry.setMap(null);

    if (mapRef) {
      const { map, maps } = mapRef;
      makeCircle(map, maps);
    }
  };

  const handleOnSampleClick = () => {
    const userCredits: number = user && user.estimatorCredits ? user.estimatorCredits : 0;
    if (!isLoggedIn) {
      localStorage.setItem('estimatorCoords', JSON.stringify(centerCoord));
      localStorage.setItem('estimatorAddress', address);
      setLoginModalOpen(true);
    } else if (userCredits > 0) {
      setUnlockRegionModalOpen(true);
    } else {
      setSubscriptionModalOpen(true);
    }
  };

  useEffect(() => {
    handleLocationChange();
  }, [customCenterLat, customCenterLng, radiusInMeter, centerCoord]);

  let nightlyRateMonthly: ADR[] = [];
  let occupancyRateMonthly: OccupancyRate[] = [];
  let revenueMonthly: Revenue[] = [];
  let bookingsPerMonthMonthly: BookingsPerMonth[] = [];

  let nightlyRateLTMAvg = 0;
  let occupancyRateLTMAvg = 0;
  let annualRevenue = 0;
  let bookingsPerMonthLTMAvg = 0;

  const apiIsLoaded = (map, maps) => {
    setMapRef({ map, maps });

    makeCircle(map, maps);
  };

  const centerPin = () => {
    return <CenterPointer lat={centerCoord.lat} lng={centerCoord.lng} />;
  };

  const markers = useMemo(() => {
    if (!apiLoading) {
      return listings.map((item, index: number) => {
        return (
          <Pointer
            key={index}
            lat={item.latitude}
            lng={item.longitude}
            data={item}
            setSubscriptionModalOpen={setSubscriptionModalOpen}
            listings={listings}
            setListings={setListings}
            isDisabled={false}
            currency={currency}
            canDelete={true}
            subscriptionModalOpen={subscriptionModalOpen}
            multipleBookingMarkersCallLoading={false}
            showRevenuePotential={true}
            isEstimator={true}
            deletedEstimatorPointer={false}
            handleOnSampleClick={handleOnSampleClick}
            hiddenListingIds={hiddenListingIds}
            addHiddenListingId={addHiddenListingId}
            removeHiddenListingId={removeHiddenListingId}
            isEstimatorLocked={isLocked}
          />
        );
      });
    }
    return;
  }, [listings, currency, isLocked, apiLoading, hiddenListingIds, lockedListingIds]);

  // estimator logic
  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (user && user.estimatorRegions) setUserSearchedRegion([...user.estimatorRegions].reverse());
  }, [user.estimatorRegions]);

  // filters
  const handleBedroomsChange = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>
  ) => {
    setFilters({
      ...filters,
      bedrooms: event.target.value as string,
    });

    setUpdateButtonDisabled(false);
  };

  const handleBathroomsChange = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>
  ) => {
    setFilters({
      ...filters,
      bathrooms: event.target.value as string,
    });

    setUpdateButtonDisabled(false);
  };

  const getIsPremiumValue = listingID => {
    for (let listing of listings) {
      if (listing.listingID === listingID) {
        return true;
      }
    }
    return false;
  };

  const getDataForDataGrid = (params: any) => {
    for (let listing of listings) {
      if (listing.listingID === params.row.listingID) {
        return listing;
      }
    }
  };

  // leaderboard
  const listViewCols: GridColDef[] = [
    {
      field: 'listingID',
      headerName: 'Listing ID',
      width: 200,
      hide: true,
    },
    {
      field: 'thumbnail_url',
      headerName: 'Listing',
      width: 100,
      renderCell: (params: any) => {
        const data = getDataForDataGrid(params);
        if (!data) return <></>;

        return (
          <Pointer
            lat={-1}
            lng={-1}
            data={data}
            listings={listings}
            setListings={setListings}
            setSubscriptionModalOpen={setSubscriptionModalOpen}
            isDisabled={lockedListingIds.includes(params.row.listingID)}
            currency={currency}
            canDelete={!lockedListingIds.includes(params.row.listingID)}
            subscriptionModalOpen={subscriptionModalOpen}
            multipleBookingMarkersCallLoading={false}
            showRevenuePotential={true}
            isEstimator={true}
            deletedEstimatorPointer={
              params.row.id < 0 ? false : listings[params.row.listingID]?.visible === false
            }
            iconComponent={
              <Card style={{ borderRadius: 5 }}>
                <CardMedia component="img" height="40" image={params.value} alt="Listing Image" />
              </Card>
            }
            estimatorSampleListingData={params.row.estimatorSampleListingData}
            handleOnSampleClick={handleOnSampleClick}
            hiddenListingIds={hiddenListingIds}
            addHiddenListingId={addHiddenListingId}
            removeHiddenListingId={removeHiddenListingId}
            isEstimatorLocked={isLocked}
          />
        );
      },
      sortable: false,
    },
    {
      field: 'avgRevenue',
      headerName: 'Revenue',
      flex: 1,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
      sortable: isLocked ? false : true,
    },
    {
      field: 'avgOccupancyRate',
      headerName: 'Occupancy Rate (Last 12 months average)',
      flex: 1,
      renderCell: (params: any) => {
        const isDataHidden: boolean = lockedListingIds.includes(params.row.listingID + '');
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 67 : params.value != -1 ? params.value : 'N/A'}%
          </Typography>
        );
      },
      sortable: isLocked ? false : true,
    },
    {
      field: 'avgNightlyRate',
      headerName: 'Nightly Rate (Last 12 months average)',
      flex: 1,
      renderCell: (params: any) => {
        const isDataHidden: boolean = lockedListingIds.includes(params.row.listingID + '');
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {exchangeRates[currency].symbol}
            {isDataHidden
              ? '123'
              : params.value != -1
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'N/A'}
          </Typography>
        );
      },
      sortable: isLocked ? false : true,
    },
    {
      field: 'activeDays',
      headerName: 'Active Days (Last 12 months)',
      flex: 1,
      renderCell: (params: any) => {
        const isDataHidden: boolean = lockedListingIds.includes(params.row.listingID + '');

        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 300 : params.value != -1 ? params.value : 'N/A'}
          </Typography>
        );
      },
      sortable: isLocked ? false : true,
    },
  ];

  // fetch sample data and get user loation
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      position => {
        setCustomCenterLat(position.coords.latitude);
        setCustomCenterLng(position.coords.longitude);
        setLocationAllowed(true);
      },
      error => {
        console.error('Error Code = ' + error.code + ' - ' + error.message);
      }
    );
  }, []);

  // url coordinate query
  useEffect(() => {
    if (window.google) {
      let coordQuery = estimatorQuery.coordinate;
      const addressQuery = estimatorQuery.address;
      const bedrooms = estimatorQuery.bedrooms === '' ? '2' : estimatorQuery.bedrooms;
      const bathrooms = estimatorQuery.bathrooms === '' ? '2' : estimatorQuery.bathrooms;
      setFilters({ ...filters, bedrooms, bathrooms });

      if (!addressQuery && !coordQuery) return;

      if (
        coordQuery === `(${sampleEstimatorCenterCoord.lat},${sampleEstimatorCenterCoord.lng})` ||
        addressQuery === sampleEstimatorMarket
      )
        return;

      setSearchBarValue({
        address: addressQuery ? addressQuery : coordQuery,
      });

      if (coordQuery) {
        const latString = coordQuery.split(',')[0].split('(')[1];
        const lngString = coordQuery.split(',')[1].split(')')[0];
        setCenterCoord({ lat: parseFloat(latString), lng: parseFloat(lngString) });
        setAddress('');
      } else if (addressQuery) {
        (async () => {
          const coord = await geocodeAddress(addressQuery, true);
          if (
            coord &&
            Array.isArray(coord) &&
            coord[0] !== 0 &&
            coord[1] !== 0 &&
            coord[0] !== sampleEstimatorCenterCoord.lat &&
            coord[1] !== sampleEstimatorCenterCoord.lng
          ) {
            addressQuery && setAddress(addressQuery);
            setTimeout(() => {
              setCenterCoord(prev => ({ lat: coord[0], lng: coord[1] }));
            }, 3000);
          } else {
            setSearchBarValue({ address: sampleEstimatorMarket });
          }
        })();
      }
    }
    return;
  }, [window.google]);

  // set custom location
  const setCustomLocationApiIsLoaded = (map, maps) => {
    const drawingManager = new maps.drawing.DrawingManager({
      drawingControl: false,
      circleOptions: {
        editable: true,
        draggable: false,
        clickble: false,
      },
    });

    drawingManager.setMap(map);

    const circle: any = new google.maps.Circle({
      strokeColor: '#000022',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#000022',
      fillOpacity: 0.35,
      map: map,
      radius: 5000,
      center: map.getCenter(),
      editable: true,
    });

    setCircleCopy(circle);

    map.addListener('dragend', () => {
      circle.setCenter(map.getCenter());
    });

    circle.addListener('radius_changed', () => {
      setRadiusInMeter(circle.radius);
    });

    circle.addListener('center_changed', () => {
      const centerLat = circle.center.lat();
      const centerLng = circle.center.lng();
      setRadiusInMeter(circle.radius);
      setCustomCenterLat(centerLat);
      setCustomCenterLng(centerLng);
    });
  };

  const handleCustomLocationMapChange = useCallback(({ center }) => {
    const centerLat: number = center.lat();
    const centerLng: number = center.lng();
    setCustomCenterLat(centerLat);
    setCustomCenterLng(centerLng);
  }, []);

  const setLocation = () => {
    setCustomLocationModalOpen(false);
    setCenterCoord({ lat: customCenterLat, lng: customCenterLng });
  };

  const locationPin = useMemo(() => {
    return (
      <div>
        <div className={classes.ripple}></div>
        <PinDropIcon
          style={{
            fontSize: 40,
            position: 'absolute',
            top: -20,
            left: -20,
          }}
        />
      </div>
    );
  }, [customCenterLat, customCenterLng]);

  // Exports

  const downloadPdf = toPdf => {
    setDownloadTriggered(true);
    setTimeout(async () => {
      await toPdf();
      exportData();
      setDownloadTriggered(false);
    }, 2000);
    mixpanel.track('downloadEstimatorPdf');
  };

  const exportData = () => {
    const data = getListingCSVDataAsArray(listings, currency);
    makeDownloadableBlob(data, `${generateDownloadName(pdfTitle)}_leaderboard_data_${currency}`);
  };

  if (serverError) {
    postOnSlack(
      'customer-dump',
      `[SERVER ERROR IN ESTIMATOR]: user: ${user.email}, address: ${address}`
    );
  }

  const toggleDrawer = () => {
    setAdvancedFilterOpen(!advancedFilterOpen);
  };

  const resetAdvancedFilter = () => {
    setAdvancedFilterVal(prevAdvancedFilterVal => ({
      ...prevAdvancedFilterVal,
      roomType: ['all'],
      bedrooms: [100],
      bathrooms: [100],
      revenueFilter: ['all'],
      priceTierFilter: ['all'],
      accommodates: [1, 16],
      minimum_nights: [1, 30],
      number_of_reviews: 'all',
      rating: [3.9, 5],
      amenities: {
        pool: null,
        parking: null,
        hot_tub: null,
        gym: null,
        pets: null,
        dryer: null,
        washer: null,
        kitchen: null,
        ac: null,
        heating: null,
        wifi: null,
        tv: null,
        bbq_grill: null,
        fireplace: null,
        breakfast: null,
        beachfront: null,
        amazing_views: null,
      },
      hostType: 'both',
    }));
    setAdvancedFilterActive(false);
  };

  const applyFilter = () => {
    if (isLocked) {
      setAdvancedFilterActive(false);
      handleOnSampleClick();
    } else {
      setUpdateButtonDisabled(false);
      handleUpdateButtonClick();
    }
  };

  console.log('Filter active, ', advancedFilterActive);

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={4}
        style={{ minHeight: '80vh', padding: matches ? '20px' : '40px' }}
      >
        <Helmet>
          <meta charSet="utf-8" />
          <title>Airbnb Profit Calculator: How much can I make on Airbnb?</title>
        </Helmet>

        {serverError && (
          <Grid style={{ width: '90%', textAlign: 'center', margin: 'auto' }}>
            <Alert severity="error" style={{ marginTop: 10 }}>
              Something went wrong! Please refresh the page!
            </Alert>
          </Grid>
        )}

        {/* {isLoggedIn && <RedirectModal dashboardName='Airbnb Calculator' />} */}

        {isLocked && isLoggedIn && (
          <Grid container direction="column" justifyContent="center" alignItems="center" item>
            <Grid item>
              <UnlockCTAButton
                handleClick={() => {
                  if (!isLoggedIn) {
                    localStorage.setItem('estimatorCoords', JSON.stringify(centerCoord));
                    localStorage.setItem('estimatorAddress', address);
                    setLoginModalOpen(true);
                  } else if (user.estimatorCredits < 1) {
                    setSubscriptionModalOpen(true);
                  } else {
                    setUnlockRegionModalOpen(true);
                  }
                }}
                text={'Unlock This Region'}
              />
            </Grid>
          </Grid>
        )}

        <ToastContainer position="bottom-left" newestOnTop style={{ marginTop: '40px' }} />

        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          item
        >
          <RealtorForm
            user={user}
            showRealtorForm={showRealtorForm}
            setshowRealtorForm={setshowRealtorForm}
            successAlert={successAlert}
            handleOnSampleClick={handleOnSampleClick}
            matches={matches}
            searchBarValue={searchBarValue}
            customCenterLat={customCenterLat}
            customCenterLng={customCenterLng}
          />
          <Grid item container direction="column" spacing={2} xs={12} md={6} lg={8}>
            {!user ? (
              <Skeleton variant="rect" animation="wave" width="100%" height={50} />
            ) : (
              <Grid container item direction="row" spacing={2} xs={12}>
                <Grid item xs={10}>
                  <div id="estimator-searchBar">
                    <Autocomplete
                      id="estimator-search"
                      open={open}
                      classes={{
                        option: classes.options,
                      }}
                      onOpen={() => {
                        setOpen(true);
                        setOptions(e => {
                          let tempAddressObjs: AddressObj[] = [
                            { address: 'Set location on map', isDefaultOption: true },
                          ];

                          user.estimatorRegions &&
                            userSearchedRegion.forEach((region: EstimatorRegions) => {
                              const endedAtDate = new Date(
                                region.ended_at_dates[region.ended_at_dates.length - 1]
                              );
                              // calculate difference in days between endedAtDate and today
                              const daysLeft: number = moment(endedAtDate).diff(moment(), 'days');

                              const expirationMessage: string =
                                daysLeft >= 0
                                  ? daysLeft === 0
                                    ? '(Expires Today)'
                                    : daysLeft === 1
                                    ? `(1 day left)`
                                    : `(${daysLeft} days left)`
                                  : '(Expired)';

                              if (region.address) {
                                tempAddressObjs.push({
                                  address: region.address,
                                  isSearchedOption: true,
                                  daysLeft: expirationMessage,
                                });
                              } else {
                                tempAddressObjs.push({
                                  address: region.coordinate,
                                  isSearchedOption: true,
                                  isCustom: true,
                                  daysLeft: expirationMessage,
                                });
                              }
                            });
                          return tempAddressObjs;
                        });
                      }}
                      onClose={() => {
                        setOpen(false);
                      }}
                      filterOptions={(options, params) => {
                        return options;
                      }}
                      getOptionLabel={option => {
                        if (option.isDefaultOption)
                          return `(${centerCoord.lat}, ${centerCoord.lng})`;
                        return option.address;
                      }}
                      renderOption={(props, option) => {
                        if (props.isDefaultOption) {
                          return (
                            <Typography>
                              <IconButton
                                style={{
                                  paddingTop: 3,
                                  paddingBottom: 3,
                                  paddingLeft: 0,
                                }}
                              >
                                <PinDropIcon
                                  style={{
                                    fontSize: 40,
                                    display: 'inline-block',
                                    marginBottom: '3px',
                                  }}
                                />
                              </IconButton>
                              Set location on map
                            </Typography>
                          );
                        }
                        if (props.isSearchedOption) {
                          return (
                            <Typography>
                              <IconButton
                                style={{
                                  paddingTop: 3,
                                  paddingBottom: 3,
                                  paddingLeft: 0,
                                }}
                              >
                                <HistoryIcon
                                  style={{
                                    fontSize: 40,
                                    display: 'inline-block',
                                    marginBottom: '3px',
                                  }}
                                />
                              </IconButton>
                              {props.address}
                              <span style={{ marginLeft: '15px', color: '#ccc', fontSize: '15px' }}>
                                {props?.daysLeft}
                              </span>
                            </Typography>
                          );
                        } else {
                          return props.address;
                        }
                      }}
                      options={options}
                      loading={loading}
                      onChange={(_, value) => handleSearchChange(_, value as AddressObj)} // this one being called after user click the value in the dropdown
                      onInputChange={lodash.debounce(
                        (value, event) => handleInputChange(value, event, userSearchedRegion),
                        600
                      )} // this one being called whenever user inputs a letter
                      noOptionsText={'No search result'}
                      loadingText={returnLoadingText(emptySearchBar)}
                      defaultValue={{ address: sampleEstimatorMarket }}
                      value={searchBarValue}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Search for an address"
                          InputLabelProps={{
                            shrink: true,
                            color: 'primary',
                          }}
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {!emptySearchBar && loading ? (
                                  <CircularProgress color="primary" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </div>
                </Grid>

                <Grid item xs={2}>
                  {geocodingLoading && <CircularProgress />}
                </Grid>
              </Grid>
            )}

            <Grid container item direction="row" style={{ gap: 12 }}>
              <Grid item>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="bedrooms">Bedrooms</InputLabel>
                  <Select
                    labelId="bedrooms"
                    id="bedrooms"
                    value={filters.bedrooms}
                    label="Bedrooms"
                    onChange={handleBedroomsChange}
                    style={{ minWidth: '95px' }}
                  >
                    {Object.keys(bedrooms).map((bedroom, index) => (
                      <MenuItem key={index} value={bedroom}>
                        {bedrooms[bedroom]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="bathrooms">Bathrooms</InputLabel>
                  <Select
                    labelId="bathrooms"
                    id="bathrooms"
                    value={filters.bathrooms}
                    label="Bathrooms"
                    onChange={handleBathroomsChange}
                    style={{ minWidth: '95px' }}
                  >
                    {Object.keys(bathrooms).map((bathroom, index) => (
                      <MenuItem key={index} value={bathroom}>
                        {bathrooms[bathroom]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item>
                <Badge badgeContent={advancedFilterActive ? '✔' : null} color="primary">
                  <Button
                    className={classes.advanceFilterButton}
                    onClick={handleOnAdvanceFilterClick}
                  >
                    <TuneIcon sx={{ marginRight: '5px' }} />
                    More
                  </Button>
                </Badge>
              </Grid>

              <Grid item>
                <Button
                  id="estimatorFilterBtn"
                  className={clsx({
                    [classes.button]: !updateButtonDisabled && !apiLoading,
                    [classes.buttonDisabled]: updateButtonDisabled || apiLoading,
                  })}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={handleUpdateButtonClick}
                  disabled={updateButtonDisabled || apiLoading}
                >
                  Update
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid container item direction="row" justify="flex-end" xs={12} md={6} lg={3} spacing={1}>
            <Grid item xs={2} style={{ flexBasis: 0 }}>
              {downloadTriggered || apiLoading || listings.length === 0 ? (
                <CircularProgress size={40} />
              ) : (
                <ReactToPdf
                  targetRef={pdfRef}
                  x={10}
                  y={10}
                  scale={0.9}
                  options={{
                    format: [210, 460],
                  }}
                  filename={generateDownloadName(pdfTitle)}
                >
                  {({ toPdf }) => (
                    <InfoTooltip
                      title={
                        <TooltipCard
                          content={`Download a CSV & PDF file with historical performance data of individual listings in this market. If you are not subscribed to the market, you will get the sample data.`}
                        />
                      }
                    >
                      <IconButton
                        style={{
                          borderRadius: '4px',
                          background: COLOR_PRIMARY_DARK,
                        }}
                        onClick={() => {
                          if (!isLocked) {
                            downloadPdf(toPdf);
                          } else {
                            if (user.estimatorCredits > 0) {
                              setUnlockRegionModalOpen(true);
                            } else {
                              setSubscriptionModalOpen(true);
                            }
                          }
                        }}
                      >
                        <FileDownloadOutlinedIcon sx={{ color: '#fff' }} />
                      </IconButton>
                    </InfoTooltip>
                  )}
                </ReactToPdf>
              )}
            </Grid>
          </Grid>
        </Grid>

        {notEnoughListingsFlag && (
          <Grid item>
            <Alert severity="error">
              Sorry, we dont have enough data for that property size near the search address. Try to
              change the number of bedrooms or the applied filters.
            </Alert>
          </Grid>
        )}

        <Grid container direction="column" item style={{ position: 'relative', top: 0, left: 0 }}>
          {matches && (
            <Grid container direction="row" spacing={1} item style={{ marginBottom: 10 }}>
              <Grid item xs={kpiCardView[0] === 'r' ? 12 : 4}>
                {apiLoading ? (
                  <LoadingCard color={COLOR_PRIMARY_LIGHT} height={150} />
                ) : (
                  <KeyMetricsCard
                    title={'Revenue'}
                    listings={listings}
                    marketData={AggregatedMarketRevenue}
                    historicalMarketLevelData={historicalMarketLevelData}
                    color={COLOR_PRIMARY_LIGHT}
                    tooltip={{
                      title: 'Revenue',
                      content: [
                        `What’s the median revenue a property in this market generated over the last 12 months?`,
                        '',
                        `This Revenue figure represents the median over the past 12 months and does not include taxes, service fees, or additional guest fees.`,
                      ],
                    }}
                    isDisabled={isLocked}
                    currency={currency}
                    handleOnSampleClick={handleOnSampleClick}
                    // lastMonthData={lastMonthFreeData.revenue}
                    isUserAllowed={!isLocked}
                    renderIn="estimator"
                    handleKpiCardView={handleKpiCardView}
                  />
                )}
              </Grid>

              <Grid item xs={kpiCardView[1] === 'r' ? 12 : 4}>
                {apiLoading ? (
                  <LoadingCard color={COLOR_ACCENT_1} height={150} />
                ) : (
                  <KeyMetricsCard
                    title={'Occupancy Rate'}
                    listings={listings}
                    marketData={AggregatedMarketOccupancy}
                    historicalMarketLevelData={historicalMarketLevelData}
                    color={COLOR_ACCENT_1}
                    tooltip={{
                      title: 'Occupancy (Median)',
                      content: [
                        `How often was a typical active property occupied over the last 12 months?`,
                      ],
                    }}
                    isDisabled={isLocked}
                    currency={currency}
                    handleOnSampleClick={handleOnSampleClick}
                    isUserAllowed={!isLocked}
                    // lastMonthData={lastMonthFreeData.occupancy_rate}
                    renderIn="estimator"
                    handleKpiCardView={handleKpiCardView}
                  />
                )}
              </Grid>

              <Grid item xs={kpiCardView[2] === 'r' ? 12 : 4}>
                {apiLoading ? (
                  <LoadingCard color={COLOR_ACCENT_2} height={150} />
                ) : (
                  <KeyMetricsCard
                    title={'Nightly Rate'}
                    listings={listings}
                    marketData={AggregatedMarketDailyRate}
                    historicalMarketLevelData={historicalMarketLevelData}
                    color={COLOR_ACCENT_2}
                    tooltip={{
                      title: 'Nightly Rate (Median)',
                      content: [
                        `How much did hosts in this market charge per day over the last 12 months?`,
                      ],
                    }}
                    isDisabled={isLocked}
                    currency={currency}
                    handleOnSampleClick={handleOnSampleClick}
                    isUserAllowed={!isLocked}
                    // lastMonthData={lastMonthFreeData.daily_rate}
                    renderIn="estimator"
                    handleKpiCardView={handleKpiCardView}
                  />
                )}
              </Grid>
            </Grid>
          )}

          {!matches && (
            <Grid container direction="row" spacing={1} item style={{ marginBottom: 10 }}>
              <Grid item xs={12} md={4} lg={4}>
                {apiLoading ? (
                  <LoadingCard color={COLOR_PRIMARY_LIGHT} height={150} />
                ) : (
                  <KeyMetricsCard
                    title={'Revenue'}
                    listings={listings}
                    marketData={AggregatedMarketRevenue}
                    historicalMarketLevelData={historicalMarketLevelData}
                    color={COLOR_PRIMARY_LIGHT}
                    tooltip={{
                      title: 'Revenue',
                      content: [
                        `What’s the median revenue a property in this market generated over the last 12 months?`,
                        '',
                        `This Revenue figure represents the median over the past 12 months and does not include taxes, service fees, or additional guest fees.`,
                      ],
                    }}
                    isDisabled={isLocked}
                    currency={currency}
                    handleOnSampleClick={handleOnSampleClick}
                    isUserAllowed={!isLocked}
                    // lastMonthData={lastMonthFreeData.revenue}
                    renderIn="estimator"
                    handleKpiCardView={handleKpiCardView}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={4} lg={4}>
                {apiLoading ? (
                  <LoadingCard color={COLOR_ACCENT_1} height={150} />
                ) : (
                  <KeyMetricsCard
                    title={'Occupancy Rate'}
                    listings={listings}
                    marketData={AggregatedMarketOccupancy}
                    historicalMarketLevelData={historicalMarketLevelData}
                    color={COLOR_ACCENT_1}
                    tooltip={{
                      title: 'Occupancy (Median)',
                      content: [
                        `How often was a typical active property occupied over the last 12 months?`,
                      ],
                    }}
                    isDisabled={isLocked}
                    currency={currency}
                    handleOnSampleClick={handleOnSampleClick}
                    isUserAllowed={!isLocked}
                    // lastMonthData={lastMonthFreeData.occupancy_rate}
                    renderIn="estimator"
                    handleKpiCardView={handleKpiCardView}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={4} lg={4}>
                {apiLoading ? (
                  <LoadingCard color={COLOR_ACCENT_2} height={150} />
                ) : (
                  <KeyMetricsCard
                    title={'Nightly Rate'}
                    listings={listings}
                    marketData={AggregatedMarketDailyRate}
                    historicalMarketLevelData={historicalMarketLevelData}
                    color={COLOR_ACCENT_2}
                    tooltip={{
                      title: 'Nightly Rate (Median)',
                      content: [
                        `How much did hosts in this market charge per day over the last 12 months?`,
                      ],
                    }}
                    isDisabled={isLocked}
                    currency={currency}
                    handleOnSampleClick={handleOnSampleClick}
                    isUserAllowed={!isLocked}
                    // lastMonthData={lastMonthFreeData.daily_rate}
                    renderIn="estimator"
                    handleKpiCardView={handleKpiCardView}
                  />
                )}
              </Grid>
            </Grid>
          )}

          {/* desktop view */}

          {!matches && (
            <Grid
              container
              direction="row"
              spacing={2}
              item
              style={{ position: 'relative', top: '0', left: '0' }}
            >
              <Grid item xs={12} md={6} lg={4}>
                {apiLoading ? (
                  <LoadingCard height={185} />
                ) : (
                  <EstimatorCalculator
                    isDisabled={isLocked}
                    currency={currency}
                    user={user}
                    setSimulateROIPopupOpen={setSimulateROIPopupOpen}
                    setUnlockRegionModalOpen={setUnlockRegionModalOpen}
                    setSubscriptionModalOpen={setSubscriptionModalOpen}
                    address={searchBarValue.address}
                    coordinate={`(${centerCoord.lat}, ${centerCoord.lng})`}
                    matches={matches}
                    simulateROIPopupOpen={simulateROIPopupOpen}
                    revenueMonthly={dynamicAnnualRevenueForCalculator / 12}
                    annualRevenue={dynamicAnnualRevenueForCalculator}
                    nightlyRate={nightlyRateLTMAvg}
                    occupancyRate={occupancyRateLTMAvg}
                    bookingsPerMonth={bookingsPerMonthLTMAvg}
                    filters={filters}
                    estimatorInvestmentCalculatorInputQueries={
                      estimatorInvestmentCalculatorInputQueries
                    }
                    isPdf={false}
                  />
                )}
              </Grid>

              <Grid container direction="row" justify="space-between" item xs={12} md={6} lg={8}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <div className={classes.mapCardContainer} style={{ flexDirection: 'column' }}>
                        <div className={classes.mapTopRightToolbarContainer}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <InfoTooltip
                              title={
                                <Card>
                                  <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                                    <Grid container direction="column" alignItems="flex-start">
                                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                                        The map shows the location of the comps and their annual
                                        revenue based on the property you searched.
                                      </Typography>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              }
                            >
                              <Typography className={classes.invisbleMarkerDataText}>
                                <IconButton className={classes.iconButton}>
                                  <InfoOutlinedIcon style={infoIconStyle} />
                                  {(apiLoading || listings.length == 0) && (
                                    <CircularProgress
                                      style={{ marginBottom: 10, marginTop: -5, marginLeft: 10 }}
                                    />
                                  )}
                                </IconButton>
                              </Typography>
                            </InfoTooltip>
                            {!apiLoading && (
                              <Typography variant="body1" style={{ fontWeight: 500 }}>
                                Comparable Listings
                              </Typography>
                            )}
                          </Box>
                          {isLocked && (
                            <Chip
                              label={isLoggedIn ? 'Unlock All Comps' : 'Unlock More Insights'}
                              variant="outlined"
                              color="success"
                              onClick={() => handleOnSampleClick()}
                              sx={{
                                height: 40,
                                fontSize: 16,
                                borderRadius: 900,
                                fontWeight: 600,
                                borderWidth: 'medium',
                              }}
                            />
                          )}

                          <CustomSwitch active={active} setActive={setActive} />
                        </div>
                        <Box
                          sx={{
                            height: '50px',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '20px',
                          }}
                        >
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setshowRealtorForm(true);
                            }}
                            style={{
                              backgroundColor: COLOR_PRIMARY_2,
                              color: SIDEBAR_COLOR,
                              borderRadius: '20px',
                            }}
                          >
                            Get matched with an investor-focused agent in{' '}
                            {searchBarValue.address === 'Set location on map'
                              ? ' your custom market'
                              : searchBarValue.address}
                          </Button>
                        </Box>
                      </div>
                      {/* //& map with markers */}
                      <Paper id="map" className={classes.mapContainer}>
                        {active.map && (
                          <GoogleMapReact
                            bootstrapURLKeys={{
                              key: GOOGLE_MAPS_API_KEY,
                              libraries: ['drawing', 'places', 'visualization'],
                              id: 'dashboard',
                            }}
                            // center={{ lat: search.data.lat, lng: search.data.lng }}
                            center={{ lat: centerCoord.lat, lng: centerCoord.lng }}
                            options={mapOptions}
                            zoom={12}
                            resetBoundsOnResize
                            yesIWantToUseGoogleMapApiInternals
                            onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps)}
                          >
                            {centerPin()}
                            {markers}
                          </GoogleMapReact>
                        )}

                        {active.list && (
                          <div className={classes.leaderboardContainer}>
                            {!isLocked ? (
                              <DataGrid
                                sortingOrder={['desc']}
                                rows={listViewData}
                                columns={listViewCols}
                                disableColumnFilter={true}
                                disableColumnMenu={true}
                                initialState={{
                                  sorting: {
                                    sortModel: [{ field: 'avgRevenue', sort: 'desc' }],
                                  },
                                }}
                                getRowClassName={params => {
                                  return params.row.isHidden ? classes.hiddenRow : '';
                                }}
                              />
                            ) : (
                              <DataGrid
                                sortingOrder={['desc']}
                                rows={listViewData}
                                columns={listViewCols}
                                disableColumnFilter={true}
                                disableColumnMenu={true}
                                // className={classes.datagridBlur}
                                sortModel={[{ field: 'id', sort: 'desc' }]}
                              />
                            )}
                            {/* {isLocked && <div className="datagrid-overlay--mobile" />} */}
                          </div>
                        )}
                      </Paper>
                      {!apiLoading && (
                        <Box
                          sx={{ paddingTop: '10px', display: 'flex', justifyContent: 'flex-end' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography style={{ paddingRight: '10px' }}>
                              1-{listings.length} out of 40
                            </Typography>
                            <InfoTooltip
                              title={
                                <TooltipCard
                                  title="Expand your dataset"
                                  content={
                                    !isFetchMoreCompsButtonDisabled
                                      ? 'Click to add 10 additional comps at a time upto a 30 additional comps'
                                      : isLocked
                                      ? 'Click to add 10 additional comps at a time upto a 30 additional comps'
                                      : 'Maximum number of comps have been fetched.'
                                  }
                                  link=""
                                />
                              }
                            >
                              <Chip
                                label={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <AddIcon /> Add more comps
                                  </Box>
                                }
                                variant="outlined"
                                // color="success"
                                onClick={() => {
                                  if (!isFetchMoreCompsButtonDisabled && !isLocked) {
                                    handleFetchMoreComps();
                                  }
                                  if (isLocked) {
                                    handleOnSampleClick();
                                  }
                                }}
                                sx={{
                                  height: 40,
                                  fontSize: 16,
                                  borderRadius: 900,
                                  fontWeight: 600,
                                  borderWidth: 'medium',
                                  cursor: 'pointer',
                                  marginRight: '10px',
                                  color: isFetchMoreCompsButtonDisabled
                                    ? COLOR_TEXT_3
                                    : COLOR_SUCCESS_1,
                                  borderColor: isFetchMoreCompsButtonDisabled
                                    ? COLOR_TEXT_3
                                    : COLOR_SUCCESS_1,
                                }}
                              />
                            </InfoTooltip>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                    {/* Nearby Searches Suggestion */}
                    {nearbySearches.length > 3 && (
                      <Box>
                        <Typography variant="h5" style={{ marginLeft: '20px' }}>
                          Nearby Search Activity
                        </Typography>
                        <Box
                          sx={{
                            height: '180px',
                            margin: '10px 20px',
                            display: 'flex',
                            overflowX: 'auto',
                            gap: '10px',
                          }}
                        >
                          {nearbySearches.map(market => {
                            let coordsString = market.coords;
                            let regexForPoint = /POINT\(([^ ]+) ([^ ]+)\)/;
                            let coordinates = coordsString.match(regexForPoint);
                            const latitude = parseFloat(coordinates[1]);
                            const longitude = parseFloat(coordinates[2]);
                            return (
                              <EstimatorSuggestionCard
                                bedrooms={market.bedrooms}
                                occupancy={market.occupancy_rate}
                                revenue={market.revenue}
                                dailyRate={market.daily_rate}
                                link={`https://app.airbtics.com/airbnb-calculator?estimatorAddress=${market.address}`}
                                // link={`http://localhost:3000/airbnb-calculator?estimatorCoord=(${longitude},${latitude})`}
                                name={market.address}
                                currency={currency}
                                setSearchBarValue={setSearchBarValue}
                                setCenterCoord={setCenterCoord}
                                latitude={latitude}
                                longitude={longitude}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Mobile View */}

          {matches && (
            <Grid container direction="column" spacing={4} item>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <div className={classes.mapCardContainer} style={{ flexDirection: 'column' }}>
                      <div className={classes.mapTopRightToolbarContainer}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <InfoTooltip
                            title={
                              <Card>
                                <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                                  <Grid container direction="column" alignItems="flex-start">
                                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                                      &#8226; The map only shows 500 listings at a time. If you
                                      don't see your listing, try zoning in.
                                      <br />
                                      <br />
                                      &#8226; The green, yellow and red marker colors represent the
                                      type of listings. Green - Entire place, Orange - Private Room,
                                      Red - Hotel or Shared rooms.
                                    </Typography>
                                  </Grid>
                                </CardContent>
                              </Card>
                            }
                          >
                            <Typography className={classes.invisbleMarkerDataText}>
                              <IconButton className={classes.iconButton}>
                                <InfoOutlinedIcon style={infoIconStyle} />
                              </IconButton>
                              {(apiLoading || listings.length == 0) && (
                                <CircularProgress
                                  style={{ marginBottom: 10, marginTop: 10, marginLeft: 10 }}
                                />
                              )}
                            </Typography>
                          </InfoTooltip>
                          {!apiLoading && (
                            <Typography variant="body1" style={{ fontWeight: 500 }}>
                              Comparable Listings
                            </Typography>
                          )}
                        </Box>
                        {isLocked && (
                          <Chip
                            label="Unlock all the Comps"
                            variant="outlined"
                            color="success"
                            onClick={() => handleOnSampleClick()}
                            sx={{
                              height: 40,
                              fontSize: 16,
                              borderRadius: 900,
                              fontWeight: 600,
                              borderWidth: 'medium',
                            }}
                          />
                        )}

                        <CustomSwitch active={active} setActive={setActive} />
                      </div>
                      <Box
                        sx={{
                          height: '50px',
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          margin: '30px 0px',
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setshowRealtorForm(true);
                          }}
                          style={{
                            backgroundColor: COLOR_PRIMARY_2,
                            color: SIDEBAR_COLOR,
                            borderRadius: '20px',
                          }}
                        >
                          Get matched with an investor-focused agent in {searchBarValue.address}
                        </Button>
                      </Box>
                    </div>
                    {/* //& map with markers */}
                    <Paper id="map" className={classes.mapContainer}>
                      {active.map && (
                        <GoogleMapReact
                          bootstrapURLKeys={{
                            key: GOOGLE_MAPS_API_KEY,
                            libraries: ['drawing', 'places', 'visualization'],
                            id: 'dashboard',
                          }}
                          // center={{ lat: search.data.lat, lng: search.data.lng }}
                          center={{ lat: centerCoord.lat, lng: centerCoord.lng }}
                          options={mapOptions}
                          zoom={12}
                          resetBoundsOnResize
                          yesIWantToUseGoogleMapApiInternals
                          onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps)}
                        >
                          {centerPin()}
                          {markers}
                        </GoogleMapReact>
                      )}

                      {active.list && (
                        <div className={classes.leaderboardContainer}>
                          {!isLocked ? (
                            <DataGrid
                              sortingOrder={['desc']}
                              rows={listViewData}
                              columns={listViewCols}
                              disableColumnFilter={true}
                              disableColumnMenu={true}
                              initialState={{
                                sorting: {
                                  sortModel: [{ field: 'avgRevenue', sort: 'desc' }],
                                },
                              }}
                              getRowClassName={params => {
                                return params.row.isHidden ? classes.hiddenRow : '';
                              }}
                            />
                          ) : (
                            <DataGrid
                              sortingOrder={['desc']}
                              rows={listViewData}
                              columns={listViewCols}
                              disableColumnFilter={true}
                              disableColumnMenu={true}
                              // className={classes.datagridBlur}
                              sortModel={[{ field: 'id', sort: 'desc' }]}
                            />
                          )}
                          {/* {isLocked && <div className="datagrid-overlay--mobile" />} */}
                        </div>
                      )}
                    </Paper>
                    {!apiLoading && (
                      <Box sx={{ paddingTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography style={{ paddingRight: '10px' }}>
                            1-{listings.length} out of 40
                          </Typography>
                          <InfoTooltip
                            title={
                              <TooltipCard
                                title="Expand your dataset"
                                content={
                                  !isFetchMoreCompsButtonDisabled
                                    ? 'Click to add 10 additional comps at a time upto a 30 additional comps'
                                    : 'Maximum number of comps have been fetched.'
                                }
                                link=""
                              />
                            }
                          >
                            <Chip
                              label={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <AddIcon /> Add more comps
                                </Box>
                              }
                              variant="outlined"
                              // color="success"
                              onClick={() => {
                                if (!isFetchMoreCompsButtonDisabled) {
                                  handleFetchMoreComps();
                                }
                              }}
                              sx={{
                                height: 40,
                                fontSize: 16,
                                borderRadius: 900,
                                fontWeight: 600,
                                borderWidth: 'medium',
                                cursor: 'pointer',
                                marginRight: '10px',
                                color: isFetchMoreCompsButtonDisabled
                                  ? COLOR_TEXT_3
                                  : COLOR_SUCCESS_1,
                                borderColor: isFetchMoreCompsButtonDisabled
                                  ? COLOR_TEXT_3
                                  : COLOR_SUCCESS_1,
                              }}
                            />
                          </InfoTooltip>
                        </Box>
                      </Box>
                    )}
                    {/* Nearby Searches Suggestion */}
                    {nearbySearches.length > 3 && (
                      <Box>
                        <Typography variant="h5" style={{ marginLeft: '20px' }}>
                          Nearby Search Activity
                        </Typography>
                        <Box
                          sx={{
                            height: '180px',
                            margin: '10px 20px',
                            display: 'flex',
                            overflowX: 'auto',
                            gap: '10px',
                          }}
                        >
                          {nearbySearches.map(market => {
                            let coordsString = market.coords;
                            let regexForPoint = /POINT\(([^ ]+) ([^ ]+)\)/;
                            let coordinates = coordsString.match(regexForPoint);
                            const latitude = parseFloat(coordinates[1]);
                            const longitude = parseFloat(coordinates[2]);
                            return (
                              <EstimatorSuggestionCard
                                bedrooms={market.bedrooms}
                                occupancy={market.occupancy_rate}
                                revenue={market.revenue}
                                dailyRate={market.daily_rate}
                                link={`https://app.airbtics.com/airbnb-calculator?estimatorAddress=${market.address}`}
                                name={market.address}
                                currency={currency}
                                setSearchBarValue={setSearchBarValue}
                                setCenterCoord={setCenterCoord}
                                latitude={latitude}
                                longitude={longitude}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                {apiLoading ? (
                  <LoadingCard height={185} />
                ) : (
                  <EstimatorCalculator
                    isDisabled={isLocked}
                    currency={currency}
                    user={user}
                    setSimulateROIPopupOpen={setSimulateROIPopupOpen}
                    setUnlockRegionModalOpen={setUnlockRegionModalOpen}
                    setSubscriptionModalOpen={setSubscriptionModalOpen}
                    address={searchBarValue.address}
                    coordinate={`(${centerCoord.lat}, ${centerCoord.lng})`}
                    matches={matches}
                    simulateROIPopupOpen={simulateROIPopupOpen}
                    revenueMonthly={dynamicAnnualRevenueForCalculator / 12}
                    annualRevenue={dynamicAnnualRevenueForCalculator}
                    nightlyRate={nightlyRateLTMAvg}
                    occupancyRate={occupancyRateLTMAvg}
                    bookingsPerMonth={bookingsPerMonthLTMAvg}
                    filters={filters}
                    estimatorInvestmentCalculatorInputQueries={
                      estimatorInvestmentCalculatorInputQueries
                    }
                    isPdf={false}
                  />
                )}
              </Grid>
            </Grid>
          )}

          {isHighlight && (
            <ViewDemoButton
              handleClick={() => removeHighlightElement('estimator-searchBar')}
              buttonText="Click here to see the demo market"
              topOffsetMobile="3%"
            />
          )}
        </Grid>
      </Grid>

      <Modal
        open={customLocationModalOpen}
        onClose={() => setCustomLocationModalOpen(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          justify="center"
          style={{ minHeight: '100vh' }}
        >
          <div className={`${classes.paperModal} modal-custom`}>
            <Grid
              container
              direction={matches ? 'column' : 'row'}
              justify="flex-end"
              alignItems="flex-end"
            >
              <IconButton
                className={classes.iconButton}
                onClick={() => setCustomLocationModalOpen(false)}
              >
                <CloseIcon style={{ color: '#f00', fontSize: 40 }} />
              </IconButton>
              <IconButton className={classes.iconButton} onClick={() => setIsInfoModalOpen(true)}>
                <NotListedLocationIcon style={{ color: '#000', fontSize: 40 }} />
              </IconButton>
            </Grid>
            <Paper className={classes.customLocationMapContainer}>
              <GoogleMapReact
                center={{ lat: customCenterLat, lng: customCenterLng }}
                options={{
                  ...mapOptions,
                  fullscreenControl: matches ? false : true,
                  zoomControlOptions: { position: matches ? 4 : 9 },
                }}
                // zoom={locationAllowed ? 16 : 2}
                zoom={12}
                resetBoundsOnResize
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => setCustomLocationApiIsLoaded(map, maps)}
                onDragEnd={map => handleCustomLocationMapChange(map)}
              >
                {!circleCopy && locationPin}
              </GoogleMapReact>
            </Paper>

            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              style={{ marginTop: 10 }}
            >
              <Typography>Move the map</Typography>

              {matches && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '100px',
                    background: '#fff',
                    borderRadius: '50px 50px 0 0',
                    boxShadow: '0px -2px 7px -1px rgba(0, 0, 0, 0.25)',
                  }}
                ></div>
              )}

              <Button
                size="small"
                variant="outlined"
                className={`${classes.button} ${classes.selectLoactionButton}`}
                onClick={() => {
                  setLocation();
                }}
              >
                Set Location
              </Button>
            </Grid>
          </div>
        </Grid>
      </Modal>

      {/* PDF */}
      {!apiLoading && listings.length > 0 && downloadTriggered && (
        <Grid
          container
          direction="column"
          spacing={4}
          ref={pdfRef as React.RefObject<HTMLDivElement>}
          className={classes.showPdf}
        >
          <Grid item xs={12}>
            <Typography variant="h4">{pdfTitle}</Typography>
            <Typography variant="subtitle1">
              {filters.bedrooms} Bedrooms, {filters.bathrooms} Bathrooms,{' '}
              {/* {filters.pool ? 'Pool Present' : 'No Pool'} */}
            </Typography>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
              Exported on {todayDate}
            </Typography>
          </Grid>

          <Grid container direction="column" item xs={12}>
            <Typography variant="h6" style={{ marginBottom: 10 }}>
              Key Metrics:
            </Typography>
            <Grid
              container
              item
              direction="row"
              justify="flex-start"
              alignItems="flex-start"
              spacing={2}
            >
              <Grid item xs={12} md={4} lg={4}>
                <KeyMetricsCard
                  title={'Revenue'}
                  listings={listings}
                  marketData={AggregatedMarketRevenue}
                  historicalMarketLevelData={historicalMarketLevelData}
                  color={COLOR_PRIMARY_LIGHT}
                  tooltip={{
                    title: 'Revenue',
                    content: [
                      `The past 12 months' total revenue of listings on the map.`,
                      `Revenue = ((Nightly Rate + Extra Guest Fees) x Number of Booked Nights) + (Cleaning Fees x Number of bookings)`,
                      `Only months having data points are taken into consideration while calculating total value.`,
                    ],
                  }}
                  isDisabled={isLocked}
                  currency={currency}
                  isUserAllowed={!isLocked}
                  handleOnSampleClick={handleOnSampleClick}
                  // lastMonthData={lastMonthFreeData.revenue}
                  renderIn="estimator"
                />
              </Grid>

              <Grid item xs={12} md={4} lg={4}>
                <KeyMetricsCard
                  title={'Occupancy Rate'}
                  listings={listings}
                  marketData={AggregatedMarketOccupancy}
                  historicalMarketLevelData={historicalMarketLevelData}
                  color={COLOR_ACCENT_1}
                  tooltip={{
                    title: 'Occupancy Rate',
                    content: [
                      `The past 12 months' average occupancy rate of listings on the map.`,
                      `Occupancy Rate = Number of booked days / Number of available days`,
                      `Only months having data points are taken into consideration while calculating average.`,
                    ],
                  }}
                  isDisabled={isLocked}
                  currency={currency}
                  handleOnSampleClick={handleOnSampleClick}
                  isUserAllowed={!isLocked}
                  // lastMonthData={lastMonthFreeData.occupancy_rate}
                  renderIn="estimator"
                />
              </Grid>

              <Grid item xs={12} md={4} lg={4}>
                <KeyMetricsCard
                  title={'Nightly Rate'}
                  listings={listings}
                  marketData={AggregatedMarketDailyRate}
                  historicalMarketLevelData={historicalMarketLevelData}
                  color={COLOR_ACCENT_2}
                  tooltip={{
                    title: 'Nightly Rate',
                    content: [
                      `The past 12 months' average nightly rate of listings on the map.`,
                      `It doesn't include a cleaning fee, service fee, or extra guest fee.`,
                      `Only months having data points are taken into consideration while calculating average.`,
                    ],
                  }}
                  isDisabled={isLocked}
                  currency={currency}
                  handleOnSampleClick={handleOnSampleClick}
                  isUserAllowed={!isLocked}
                  // lastMonthData={lastMonthFreeData.daily_rate}
                  renderIn="estimator"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" item xs={12}>
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: 10 }}>
                ROI Simulation:
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <EstimatorCalculator
                isDisabled={isLocked}
                currency={currency}
                user={user}
                setSimulateROIPopupOpen={setSimulateROIPopupOpen}
                setUnlockRegionModalOpen={setUnlockRegionModalOpen}
                setSubscriptionModalOpen={setSubscriptionModalOpen}
                address={searchBarValue.address}
                coordinate={`(${centerCoord.lat}, ${centerCoord.lng})`}
                matches={matches}
                simulateROIPopupOpen={simulateROIPopupOpen}
                revenueMonthly={dynamicAnnualRevenueForCalculator / 12}
                annualRevenue={dynamicAnnualRevenueForCalculator}
                nightlyRate={nightlyRateLTMAvg}
                occupancyRate={occupancyRateLTMAvg}
                bookingsPerMonth={bookingsPerMonthLTMAvg}
                filters={filters}
                estimatorInvestmentCalculatorInputQueries={
                  estimatorInvestmentCalculatorInputQueries
                }
                isPdf={true}
              />
            </Grid>
            <div style={{ background: 'blue', height: '200px', width: '100%' }} />
          </Grid>
        </Grid>
      )}

      {unlockRegionModalOpen && (
        <ConfirmUnlockRegionModal
          open={unlockRegionModalOpen}
          onConfirm={onUnlockModalConfirmClicked}
          onCancel={onUnlockModalCancelClicked}
          message="Would you like to unlock this report?"
        />
      )}

      {isInfoModalOpen && customLocationModalOpen && (
        <Modal
          open={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box
            sx={{
              width: '70vw',
              maxWidth: '400px',
              bgcolor: COLOR_PRIMARY_LIGHT,
              boxShadow: 24,
              p: 4,
              borderRadius: '20px',
              animation: 'fade-scale-in 0.3s ease forwards',
            }}
          >
            <Typography style={{ color: '#000' }}>
              Zoom in and move around the map to find out your neighbourhood. Click the circle
              button on top left of the map to define your own region.{' '}
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setIsInfoModalOpen(false);
              }}
              style={{
                background: '#fff',
                color: '#000',
                borderRadius: 100,
                padding: '5px auto',
                outline: 'none',
                border: 'none',
                textTransform: 'capitalize',
                marginTop: 16,
                width: '100%',
                fontSize: '18px',
                fontWeight: 'bold',
              }}
            >
              I Understood
            </Button>
          </Box>
        </Modal>
      )}

      {advancedFilterOpen && (
        <Filter
          listings={{ data: [], loading: false }}
          allListings={{ data: {}, loading: false }}
          isDisabled={isLocked}
          filterVal={advancedFilterVal}
          setFilterVal={setAdvancedFilterVal}
          applyFilter={applyFilter}
          open={advancedFilterOpen}
          toggleDrawer={toggleDrawer}
          setFilterActive={setAdvancedFilterActive}
          matches={matches}
          setFilterApplied={setAdvancedFilterApplied}
          resetFilter={resetAdvancedFilter}
          currency={currency}
          revPotentialRangeInvestment={{} as any}
          setIndividualRevenuePotentialRange={() => {}}
          priceTiers={{} as any}
          handleOnSampleClick={() => {}}
          isListingLoading={apiLoading}
          dummyMarket={null}
          isEstimatorAdvancedFilter={true}
        />
      )}
    </>
  );
};

const generateDownloadName = (address: string): string => {
  if (!address) {
    return '';
  }
  const generatedName = address.replace(/, /g, '_').replace(/ /g, '_');
  return `${generatedName}_exported_airbtics.pdf`;
};

export default EstimatorDashboard;
